import { ChevronRightIcon } from "@chakra-ui/icons";
import { useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Image,
  Select,
  Text,
} from "@chakra-ui/react";
import {
  buildWeekStrip,
  buildYearPixels,
  formatLastReadDay,
  formatLastReadDetail,
  levelFor,
} from "../helpers/readingActivity";
import { rateGoalLink } from "../helpers/goals";

const EMPTY_COLOR = "gray.700";
const LEVEL_COLORS = [EMPTY_COLOR, "#4a1e27", "#7c232c", "#b52f3a", "#ed1d24"];

const LastRead = ({ lastRead, lastReadAt, lastReadOrder, onPress }) => (
  <Box mt="5">
    <Text color="gray.500" fontSize="xs" fontWeight="bold" letterSpacing="wide">
      LAST READ
    </Text>
    <Flex
      as={onPress ? "button" : "div"}
      type={onPress ? "button" : undefined}
      align="center"
      mt="3"
      w={onPress ? "100%" : undefined}
      p={onPress ? "2" : undefined}
      mx={onPress ? "-2" : undefined}
      border="0"
      bg="transparent"
      textAlign="left"
      onClick={onPress}
      _hover={onPress ? { bg: "gray.900" } : undefined}
      _focusVisible={
        onPress
          ? { outline: "2px solid #ED1D24", outlineOffset: "2px" }
          : undefined
      }
    >
      <Image
        alt={lastRead.title}
        borderRadius="sm"
        h="62px"
        objectFit="cover"
        src={lastRead.thumbnail}
        w="44px"
      />
      <Box flex="1" minW="0" mx="3">
        <Text fontSize="md" fontWeight="semibold" noOfLines={1}>
          {lastRead.title}
        </Text>
        <Text color="gray.400" fontSize="sm" noOfLines={1}>
          {formatLastReadDetail(lastReadOrder, lastRead.story || "Comic")}
        </Text>
        <Text color="gray.400" fontSize="sm" noOfLines={1}>
          {formatLastReadDay(lastReadAt || lastRead.readingDate) ||
            lastRead.readingDateFormatted ||
            "Recently"}
        </Text>
      </Box>
      <ChevronRightIcon boxSize="6" color="gray.500" />
    </Flex>
  </Box>
);

const ReadingStreakCard = ({
  streak = 0,
  longestStreak = 0,
  activity = {},
  lastRead,
  lastReadAt,
  lastReadOrder,
  isOwnProfile = true,
  onLastReadPress,
  onOpenGoals,
  onYearChange,
  rateGoal = null,
}) => {
  const currentDays = Number(streak) || 0;
  const longestDays = Number(longestStreak) || 0;
  const currentYear = new Date().getFullYear();
  const year = Number(activity.year) || currentYear;
  const years = (activity.availableYears || [year]).map(Number);
  const days = activity.days;
  const months = useMemo(() => buildYearPixels(year, days || {}), [year, days]);
  const week = useMemo(() => buildWeekStrip(days || {}), [days]);

  return (
    <>
      <Box px={{ base: 5, md: 8 }} py={{ base: 5, md: 7 }}>
        <Flex align="center" justify="space-between" mb="3">
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
            Current streak
          </Text>
          <Text color="gray.400" fontSize={{ base: "sm", md: "md" }}>
            Best: {longestDays} {longestDays === 1 ? "day" : "days"}
          </Text>
        </Flex>

        <Flex align="center">
          <Flex align="baseline" flex="0 0 80px">
            <Text fontSize="4xl" fontWeight="bold" lineHeight="1">
              {currentDays}
            </Text>
            <Text color="gray.300" fontSize="sm" ml="1">
              {currentDays === 1 ? "day" : "days"}
            </Text>
          </Flex>
          <Grid
            flex="1"
            gap={{ base: 1, md: 2 }}
            templateColumns="repeat(7, 1fr)"
          >
            {week.map((day) => (
              <Box key={day.key} textAlign="center">
                <Text color="gray.500" fontSize="xs" mb="1">
                  {day.label}
                </Text>
                <Box
                  aria-label={`${day.key}: ${day.count} comics read`}
                  bg={
                    day.count
                      ? "red.400"
                      : day.isUpcoming
                      ? "transparent"
                      : "gray.700"
                  }
                  border={
                    day.count || !day.isUpcoming ? undefined : "1px dashed"
                  }
                  borderColor="gray.600"
                  borderRadius="md"
                  h={{ base: 7, md: 8 }}
                  mx="auto"
                  w={{ base: 7, md: 8 }}
                />
              </Box>
            ))}
          </Grid>
        </Flex>

        {!!rateGoal && (
          <Button
            color="gray.300"
            fontWeight="normal"
            mt="3"
            onClick={onOpenGoals}
            px="0"
            rightIcon={<ChevronRightIcon />}
            size="sm"
            variant="link"
          >
            {rateGoalLink(rateGoal)}
          </Button>
        )}

        {!!lastRead && (
          <LastRead
            lastRead={lastRead}
            lastReadAt={lastReadAt}
            lastReadOrder={lastReadOrder}
            onPress={
              onLastReadPress ? () => onLastReadPress(lastRead) : undefined
            }
          />
        )}
      </Box>

      <Divider borderColor="gray.700" />

      <Box px={{ base: 5, md: 8 }} py={{ base: 5, md: 7 }}>
        <Flex align="center" justify="space-between" mb="3">
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
            {isOwnProfile ? "Your year in pixels" : "Activity by day"}
          </Text>
          <Select
            aria-label="Activity year"
            bg="gray.900"
            borderColor="gray.600"
            borderRadius="full"
            onChange={(event) => onYearChange?.(Number(event.target.value))}
            size="sm"
            value={year}
            width="96px"
          >
            {years.map((optionYear) => (
              <option key={optionYear} value={optionYear}>
                {optionYear}
              </option>
            ))}
          </Select>
        </Flex>

        <Grid gap="1" templateColumns="repeat(12, minmax(0, 1fr))" mb="2">
          {months.map((month) => (
            <Text
              color="gray.400"
              fontSize="xs"
              key={month.index}
              textAlign="center"
            >
              {month.label}
            </Text>
          ))}
        </Grid>
        {Array.from({ length: months[0].blocks.length }, (_, blockIndex) => (
          <Grid
            gap="1"
            key={blockIndex}
            mb="1"
            templateColumns="repeat(12, minmax(0, 1fr))"
          >
            {months.map((month) => {
              const block = month.blocks[blockIndex];
              const label = `${month.label} ${block.firstDay}-${block.lastDay}: ${block.count} comics read`;

              return (
                <Box
                  aria-label={block.isOutside ? undefined : label}
                  bg={
                    block.isOutside
                      ? "transparent"
                      : LEVEL_COLORS[levelFor(block.count)] || EMPTY_COLOR
                  }
                  borderRadius="3px"
                  h={{ base: "10px", md: "14px" }}
                  key={`${month.index}-${blockIndex}`}
                  title={block.isOutside ? undefined : label}
                />
              );
            })}
          </Grid>
        ))}
      </Box>
    </>
  );
};

export default ReadingStreakCard;
